import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Clock, Send, Eye, ToggleLeft, ToggleRight, Truck,
  Anchor, Users, Package, X, RefreshCw, Calendar
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
];

export const DigestSettingsTab = () => {
  const [settings, setSettings] = useState(null);
  const [ports, setPorts] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const [settingsRes, portsRes] = await Promise.all([
        axios.get(`${API}/digest/settings`),
        axios.get(`${API}/regions`),
      ]);
      setSettings(settingsRes.data);
      setPorts(portsRes.data);
    } catch (e) {
      toast.error('Failed to load digest settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const updateSetting = async (updates) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/digest/settings`, updates);
      setSettings(res.data);
      toast.success('Settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const loadPreview = async () => {
    setPreviewLoading(true);
    setShowPreview(true);
    try {
      const res = await axios.get(`${API}/digest/preview`);
      setPreview(res.data);
    } catch (e) {
      toast.error('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const sendNow = async () => {
    if (!window.confirm('Send digest emails to all targeted carriers right now?')) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/digest/send-now`, {});
      if (res.data.sent) {
        toast.success(`Digest sent! ${res.data.results?.total_sent || 0} emails delivered.`);
        fetchSettings();
      } else {
        toast.error(res.data.reason || 'No emails sent');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send digest');
    } finally {
      setSending(false);
    }
  };

  const togglePort = (portId) => {
    const current = settings?.target_port_ids || [];
    const updated = current.includes(portId)
      ? current.filter(p => p !== portId)
      : [...current, portId];
    updateSetting({ target_port_ids: updated });
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="digest-settings-tab">
      {/* Status Banner */}
      <div className={`flex items-center justify-between p-5 border ${settings.enabled ? 'bg-green-50 border-green-200' : 'bg-zinc-50 border-zinc-200'}`}>
        <div className="flex items-center gap-3">
          <Calendar className={`w-6 h-6 ${settings.enabled ? 'text-green-600' : 'text-zinc-400'}`} />
          <div>
            <h3 className="font-bold text-zinc-900 text-base">
              Automated Daily Digest {settings.enabled ? 'Active' : 'Disabled'}
            </h3>
            <p className="text-sm text-zinc-500 mt-0.5">
              {settings.enabled
                ? `Sends daily at ${settings.send_time} ${TIMEZONES.find(t => t.value === settings.timezone)?.label || settings.timezone}`
                : 'Enable to automatically send load digests to carriers'}
            </p>
          </div>
        </div>
        <button
          onClick={() => updateSetting({ enabled: !settings.enabled })}
          className={`flex items-center gap-2 px-5 py-2.5 font-bold text-sm uppercase tracking-wide transition-colors ${
            settings.enabled
              ? 'bg-green-600 hover:bg-green-700 text-white'
              : 'bg-zinc-800 hover:bg-zinc-700 text-white'
          }`}
          data-testid="digest-toggle-btn"
          disabled={saving}
        >
          {settings.enabled ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
          {settings.enabled ? 'Enabled' : 'Disabled'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Schedule Settings */}
        <div className="bg-white border border-zinc-200 p-6 space-y-5">
          <h3 className="font-black text-zinc-900 uppercase text-sm tracking-wider flex items-center gap-2">
            <Clock className="w-4 h-4" /> Schedule
          </h3>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Send Time</label>
            <input
              type="time"
              value={settings.send_time}
              onChange={(e) => updateSetting({ send_time: e.target.value })}
              className="w-full px-4 py-2.5 border border-zinc-300 focus:border-zinc-900 outline-none text-sm"
              data-testid="digest-send-time"
            />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Timezone</label>
            <select
              value={settings.timezone}
              onChange={(e) => updateSetting({ timezone: e.target.value })}
              className="w-full px-4 py-2.5 border border-zinc-300 focus:border-zinc-900 outline-none text-sm bg-white"
              data-testid="digest-timezone"
            >
              {TIMEZONES.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Send Condition</label>
            <div className="flex gap-3">
              <button
                onClick={() => updateSetting({ only_new_loads: false })}
                className={`flex-1 px-4 py-2.5 text-sm font-bold border transition-colors ${
                  !settings.only_new_loads
                    ? 'bg-zinc-900 text-white border-zinc-900'
                    : 'border-zinc-300 text-zinc-600 hover:border-zinc-400'
                }`}
                data-testid="digest-all-loads-btn"
              >
                All Active Loads
              </button>
              <button
                onClick={() => updateSetting({ only_new_loads: true })}
                className={`flex-1 px-4 py-2.5 text-sm font-bold border transition-colors ${
                  settings.only_new_loads
                    ? 'bg-zinc-900 text-white border-zinc-900'
                    : 'border-zinc-300 text-zinc-600 hover:border-zinc-400'
                }`}
                data-testid="digest-new-loads-btn"
              >
                New Loads Only
              </button>
            </div>
            <p className="text-xs text-zinc-400 mt-2">
              {settings.only_new_loads
                ? 'Only sends when there are loads posted since the last digest'
                : 'Sends whenever there are any active loads on the board'}
            </p>
          </div>
        </div>

        {/* Targeting Settings */}
        <div className="bg-white border border-zinc-200 p-6 space-y-5">
          <h3 className="font-black text-zinc-900 uppercase text-sm tracking-wider flex items-center gap-2">
            <Users className="w-4 h-4" /> Targeting
          </h3>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Carrier Types</label>
            <div className="flex gap-3">
              <button
                onClick={() => updateSetting({ target_otr: !settings.target_otr })}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold uppercase border transition-colors ${
                  settings.target_otr
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'border-zinc-300 text-zinc-600 hover:border-zinc-400'
                }`}
                data-testid="digest-target-otr"
              >
                <Truck className="w-4 h-4" /> OTR
              </button>
              <button
                onClick={() => updateSetting({ target_drayage: !settings.target_drayage })}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold uppercase border transition-colors ${
                  settings.target_drayage
                    ? 'bg-orange-600 text-white border-orange-600'
                    : 'border-zinc-300 text-zinc-600 hover:border-zinc-400'
                }`}
                data-testid="digest-target-drayage"
              >
                <Anchor className="w-4 h-4" /> Drayage
              </button>
            </div>
          </div>

          {settings.target_drayage && ports.length > 0 && (
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                Target Ports {settings.target_port_ids.length === 0 && <span className="text-zinc-400 normal-case">(all ports)</span>}
              </label>
              <div className="flex flex-wrap gap-2">
                {ports.map(p => (
                  <button
                    key={p.id}
                    onClick={() => togglePort(p.id)}
                    className={`px-3 py-1.5 text-xs font-bold uppercase border transition-colors ${
                      settings.target_port_ids.includes(p.id)
                        ? 'bg-orange-600 text-white border-orange-600'
                        : 'border-zinc-300 text-zinc-600 hover:border-zinc-400'
                    }`}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
              <p className="text-xs text-zinc-400 mt-2">
                {settings.target_port_ids.length === 0
                  ? 'All drayage carriers will receive the digest'
                  : `Only carriers at selected port(s) will receive the digest`}
              </p>
            </div>
          )}

          {!settings.target_otr && !settings.target_drayage && (
            <div className="bg-amber-50 border border-amber-200 p-3 text-sm text-amber-700">
              Select at least one carrier type to send digest emails.
            </div>
          )}
        </div>
      </div>

      {/* Last Sent Info */}
      {settings.last_sent_at && (
        <div className="bg-white border border-zinc-200 p-5">
          <h3 className="font-black text-zinc-900 uppercase text-sm tracking-wider mb-3">Last Digest Sent</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-lg font-bold text-zinc-900">
                {new Date(settings.last_sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </div>
              <div className="text-xs text-zinc-500 uppercase">Date</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-zinc-900">
                {new Date(settings.last_sent_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
              </div>
              <div className="text-xs text-zinc-500 uppercase">Time</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-blue-600">{settings.last_sent_loads_count || 0}</div>
              <div className="text-xs text-zinc-500 uppercase">Loads Included</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-green-600">{settings.last_sent_recipients || 0}</div>
              <div className="text-xs text-zinc-500 uppercase">Emails Sent</div>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={loadPreview}
          className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white px-5 py-2.5 font-bold text-sm uppercase tracking-wide transition-colors"
          data-testid="digest-preview-btn"
        >
          <Eye className="w-4 h-4" /> Preview Recipients
        </button>
        <button
          onClick={sendNow}
          disabled={sending || (!settings.target_otr && !settings.target_drayage)}
          className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 font-bold text-sm uppercase tracking-wide transition-colors disabled:opacity-50"
          data-testid="digest-send-now-btn"
        >
          {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {sending ? 'Sending...' : 'Send Now'}
        </button>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <PreviewModal
          preview={preview}
          loading={previewLoading}
          onClose={() => { setShowPreview(false); setPreview(null); }}
        />
      )}
    </div>
  );
};


const PreviewModal = ({ preview, loading, onClose }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div className="bg-white w-full max-w-2xl max-h-[85vh] overflow-y-auto">
      <div className="flex justify-between items-center p-6 border-b border-zinc-200">
        <h2 className="text-xl font-black text-zinc-900">Digest Preview</h2>
        <button onClick={onClose} className="p-2 text-zinc-400 hover:text-zinc-900">
          <X className="w-5 h-5" />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="w-8 h-8 animate-spin text-zinc-400" />
        </div>
      ) : preview ? (
        <div className="p-6 space-y-5">
          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-zinc-50 border border-zinc-200 p-4 text-center">
              <div className="text-2xl font-black text-zinc-900">{preview.total_recipients}</div>
              <div className="text-xs font-bold text-zinc-500 uppercase mt-1">Total Recipients</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 p-4 text-center">
              <div className="text-2xl font-black text-blue-600">{preview.otr_recipients}</div>
              <div className="text-xs font-bold text-zinc-500 uppercase mt-1">OTR Carriers</div>
            </div>
            <div className="bg-orange-50 border border-orange-200 p-4 text-center">
              <div className="text-2xl font-black text-orange-600">{preview.drayage_recipients}</div>
              <div className="text-xs font-bold text-zinc-500 uppercase mt-1">Drayage Carriers</div>
            </div>
          </div>

          {/* Status */}
          <div className={`p-3 text-sm font-medium ${
            preview.would_send
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-amber-50 border border-amber-200 text-amber-700'
          }`}>
            {preview.would_send
              ? `Ready to send: ${preview.loads_count} load(s) to ${preview.total_recipients} carrier(s)`
              : preview.loads_count === 0
                ? 'No active loads to include in digest'
                : preview.total_recipients === 0
                  ? 'No carriers match the current targeting criteria'
                  : 'Digest is currently disabled'}
          </div>

          {/* Target Ports */}
          {preview.target_ports && preview.target_ports.length > 0 && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Target Ports</h4>
              <div className="flex flex-wrap gap-2">
                {preview.target_ports.map(p => (
                  <span key={p.id} className="px-3 py-1 bg-orange-100 text-orange-700 text-xs font-bold uppercase">
                    {p.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Loads Preview */}
          {preview.loads && preview.loads.length > 0 && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                Loads to Include ({preview.loads_count} total, showing first {Math.min(preview.loads.length, 10)})
              </h4>
              <div className="border border-zinc-200 overflow-x-auto">
                <table className="w-full text-sm" data-testid="preview-loads-table">
                  <thead className="bg-zinc-50 border-b">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-bold uppercase text-zinc-500">Ref</th>
                      <th className="px-3 py-2 text-left text-xs font-bold uppercase text-zinc-500">Pickup</th>
                      <th className="px-3 py-2 text-left text-xs font-bold uppercase text-zinc-500">Destination</th>
                      <th className="px-3 py-2 text-left text-xs font-bold uppercase text-zinc-500">Equipment</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {preview.loads.map((load) => (
                      <tr key={load.id || load.reference_no} className="hover:bg-zinc-50">
                        <td className="px-3 py-2 text-zinc-900 font-medium">#{load.reference_no}</td>
                        <td className="px-3 py-2 text-zinc-600">{load.pickup}</td>
                        <td className="px-3 py-2 text-zinc-600">{load.destination}</td>
                        <td className="px-3 py-2 text-zinc-600">{load.truck_type}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <button
            onClick={onClose}
            className="w-full bg-zinc-900 text-white py-3 font-bold uppercase text-sm"
          >
            Close Preview
          </button>
        </div>
      ) : (
        <div className="p-6 text-center text-zinc-400 py-12">Failed to load preview</div>
      )}
    </div>
  </div>
);
